import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Download, Loader2, Building2, DollarSign, Users, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/tax";
import { RevealEmail } from "@/components/RevealEmail";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { downloadFilingPdf } from "@/pages/MerchantDashboard";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPayments, { exportFilingsCsv } from "@/pages/admin/AdminPayments";
import AdminReports from "@/pages/admin/AdminReports";
import AdminArchives from "@/pages/admin/AdminArchives";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminContent from "@/pages/admin/AdminContent";
import AdminTaxConfig from "@/pages/admin/AdminTaxConfig";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import { useAuth } from "@/context/AuthContext";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white border border-slate-200 rounded-md p-6" data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#1E3A8A]" />
      </span>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
        <p className="font-mono-data text-2xl font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  </div>
);

function FilingsTab() {
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/filings"), api.get("/admin/stats")])
      .then(([fRes, sRes]) => {
        setFilings(fRes.data);
        setStats(sRes.data);
      })
      .catch(() => toast.error("Failed to load admin data"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filings.filter((f) => {
    const s = q.toLowerCase();
    return (
      f.business_name?.toLowerCase().includes(s) ||
      f.filer_email?.toLowerCase().includes(s) ||
      f.confirmation_number?.toLowerCase().includes(s)
    );
  });

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <StatCard icon={FileText} label="Total Filings" value={stats?.total_filings ?? "—"} testId="stat-total-filings" />
        <StatCard icon={Users} label="Merchants" value={stats?.unique_merchants ?? "—"} testId="stat-merchants" />
        <StatCard icon={DollarSign} label="Total Assessed" value={stats ? money(stats.total_due) : "—"} testId="stat-total-due" />
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900">Filings</h2>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business, email, #" className="pl-9" data-testid="admin-search" />
          </div>
          <Button
            onClick={() => exportFilingsCsv(setCsvBusy)}
            disabled={csvBusy}
            variant="outline"
            className="border-slate-300 shrink-0"
            data-testid="filings-export-csv"
          >
            {csvBusy ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Download className="w-4 h-4 sm:mr-2" />}
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-16 text-center bg-white" data-testid="admin-empty">
          <p className="text-slate-500">No filings found.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="admin-filings-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                <th className="px-5 py-3 font-medium">Business</th>
                <th className="px-5 py-3 font-medium hidden md:table-cell">Filer</th>
                <th className="px-5 py-3 font-medium hidden sm:table-cell">Confirmation</th>
                <th className="px-5 py-3 font-medium text-right">Total Due</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50 transition-colors" data-testid={`admin-row-${f.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                        <Building2 className="w-4 h-4 text-[#1E3A8A]" />
                      </span>
                      <div>
                        <p className="font-medium text-slate-900">{f.business_name}</p>
                        <p className="text-xs text-slate-400">{new Date(f.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 hidden md:table-cell">
                    <RevealEmail
                      masked={f.filer_email}
                      testId={`filing-email-${f.id}`}
                      onReveal={async () => {
                        const { data } = await api.get(`/admin/filings/${f.id}/email`);
                        return data.email;
                      }}
                    />
                  </td>
                  <td className="px-5 py-3 font-mono-data text-slate-500 hidden sm:table-cell">{f.confirmation_number}</td>
                  <td className="px-5 py-3 text-right font-mono-data font-medium text-slate-900">{money(f.totals?.l14)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" className="border-slate-300" onClick={() => navigate(`/filing/${f.id}`)} data-testid={`admin-view-${f.id}`}>
                        View
                      </Button>
                      <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" disabled={busy === f.id} onClick={() => downloadFilingPdf(f.id, f.confirmation_number, setBusy)} data-testid={`admin-download-${f.id}`}>
                        {busy === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const can = (p) => user?.role === "admin" || (user?.permissions || []).includes(p);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    api
      .get("/settings/access")
      .then(({ data }) => setSignupEnabled(!!data.signup_enabled))
      .catch(() => {});
  }, []);

  const tabs = [
    can("view_filings") && { value: "filings", label: "Filings", node: <FilingsTab /> },
    can("manage_payments") && { value: "payments", label: "Payments", node: <AdminPayments /> },
    can("print_reports") && { value: "reports", label: "Reports", node: <AdminReports /> },
    can("manage_archives") && { value: "archives", label: "Archives", node: <AdminArchives /> },
    can("manage_staff") && { value: "staff", label: "Staff", node: <AdminStaff /> },
    can("edit_content") && { value: "settings", label: "Front Page", node: <AdminSettings /> },
    can("edit_content") && { value: "taxconfig", label: "Tax Setup", node: <AdminTaxConfig /> },
    can("manage_content") && { value: "content", label: "News & Events", node: <AdminContent /> },
    user?.is_primary && { value: "system", label: "System", node: <AdminSystem /> },
    { value: "security", label: "Security", node: <AdminSecurity /> },
  ].filter(Boolean);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900">
          Finance &amp; Tax Office
        </h1>
        <p className="mt-2 text-slate-500">Manage filings, payments, records, and staff access.</p>

        {!signupEnabled && can("edit_content") && (
          <div
            className="mt-6 rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
            data-testid="demo-mode-notice"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-900">You're in demonstration mode</p>
              <p className="text-sm text-amber-800 mt-0.5">
                New merchant signups are turned off and a "Demonstration site" banner is showing to
                visitors. When you're ready to go live, turn signups on under Front Page.
              </p>
              <Button
                onClick={() => setActiveTab("settings")}
                className="mt-3 h-8 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="demo-notice-go-live-btn"
              >
                Go to Front Page settings
              </Button>
            </div>
          </div>
        )}

        {tabs.length === 0 ? (
          <div className="mt-8 border border-dashed border-slate-300 rounded-md p-16 text-center bg-white">
            <p className="text-slate-500">
              Your account has no dashboard permissions yet. Contact your administrator.
            </p>
          </div>
        ) : (
          <Tabs value={activeTab || tabs[0].value} onValueChange={setActiveTab} className="mt-8">
            <TabsList className="bg-slate-100 flex-wrap h-auto">
              {tabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map((t) => (
              <TabsContent key={t.value} value={t.value} className="mt-6">
                {t.node}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </main>
    </div>
  );
}
